import { listarVariaveisObservacao, selecionarFormulasPorCriterio } from '@shared/components/formula/utils/formula-contexto.util';
import { coletarEstadosFormulas } from '@shared/components/formula/utils/formula-estados.util';
import { planejarTrocaCodigoFormulas } from '@shared/components/formula/utils/troca-codigo-formulas.util';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { AtivacaoSectionCardComponent } from '@shared/components/ativacao-section-card/ativacao-section-card.component';
import { SnTributoObservacoesSectionComponent } from './sections/tributo-observacoes-section/tributo-observacoes-section.component';
import { SnTributoObservacoesFiscoSectionComponent } from './sections/tributo-observacoes-fisco-section/tributo-observacoes-fisco-section.component';
import { MAPA_CAMPOS_CSOSN_NATUREZA, SnTributoRegraSectionComponent } from './sections/tributo-regra-section/tributo-regra-section.component';
import { SnTributoExcecoesSectionComponent } from './sections/tributo-excecoes-section/tributo-excecoes-section.component';
import { FormulaEditorDialogComponent, FormulaEditorDialogData, FormulaEditorDialogResult } from '@shared/components/formula/formula-editor-dialog/formula-editor-dialog.component';
import { VariaveisDisponiveisDialogComponent, VariaveisDisponiveisDialogData, VariaveisCursorPayload } from '@shared/components/formula/variaveis-disponiveis-dialog/variaveis-disponiveis-dialog.component';
import { CsosnStruct } from '@shared/services/structs/motor-fiscal/csosn.struct';
import { ModalidadeBcOption } from '@shared/services/structs/motor-fiscal/modalidade-bc-option.struct';
import { NaturezaOperacaoIcmsSnExcecaoPendenteStruct } from '@shared/services/structs/motor-fiscal/natureza-operacao-icms-sn-excecao-pendente.struct';
import { FormulaItemStruct } from '@shared/services/structs/formula/formula-item.struct';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { FormulaCatalogoService } from '@shared/services/apis/motor-fiscal/formula-catalogo.service';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { TipoFormulaEnum } from '@shared/enums/formula/tipo-formula.enum';
import {
    aplicarObrigatoriedadeIndicadores,
    lerIndicadoresCst,
} from '@shared/constants/motor-fiscal/indicadores-campos.util';
import { environment } from '../../../../../../../environments/environment';

@Component({
    selector: 'app-natureza-operacao-icmssn-tab',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        AtivacaoSectionCardComponent,
        SnTributoRegraSectionComponent,
        SnTributoObservacoesSectionComponent,
        SnTributoObservacoesFiscoSectionComponent,
        SnTributoExcecoesSectionComponent,
    ],
    templateUrl: './natureza-operacao-icmssn-tab.component.html',
    styleUrl: './natureza-operacao-icmssn-tab.component.css',
})
export class NaturezaOperacaoIcmsSnTabComponent implements OnInit {
    @Input({ required: true }) form!: FormGroup;
    @Input() csosns: CsosnStruct[] = [];
    @Input() modalidadesBc: ModalidadeBcOption[] = [];
    @Input() modalidadesBcSt: ModalidadeBcOption[] = [];
    @Input() formulasIcmsSn: FormulaCatalogoItemStruct[] = [];
    @Input() estadosFormulas: FormulaItemStruct[] = [];
    @Input() idNaturezaOperacao: number | null = null;
    @Input() excecoesPendentes: NaturezaOperacaoIcmsSnExcecaoPendenteStruct[] = [];
    @Output() excecoesChange = new EventEmitter<NaturezaOperacaoIcmsSnExcecaoPendenteStruct[]>();

    public cfopLookupUrl = environment.urlApiMotorFiscal + 'Lookup/cfop';
    private csosnAnterior: string | null = null;
    private restaurandoCsosn = false;

    ngOnInit(): void {
        this.isAtivoControl.valueChanges.subscribe(() => this.sincronizarObrigatoriedade());
        this.csosnAnterior = this.csosnValue;
        this.icmsSnForm.get('csosn')?.valueChanges.subscribe(csosn => {
            this.sincronizarObrigatoriedade();
            this.confirmarTrocaCsosn(csosn);
        });
        this.sincronizarObrigatoriedade();
    }

    /** CSOSN exigido com o tributo ligado; os campos do código, pelo indicador do catálogo. */
    private sincronizarObrigatoriedade(): void {
        const ativo = !!this.isAtivoControl.value;
        const codigo = this.icmsSnForm.get('csosn')?.value;
        this.aplicarObrigatorio(this.icmsSnForm.get('csosn'), ativo);
        const indicadores = ativo && codigo ? lerIndicadoresCst(this.csosns.find(c => c.codigo === codigo)?.indicadores) : null;
        aplicarObrigatoriedadeIndicadores(this.icmsSnForm, indicadores, MAPA_CAMPOS_CSOSN_NATUREZA);
    }

    private aplicarObrigatorio(control: import('@angular/forms').AbstractControl | null | undefined, obrigatorio: boolean): void {
        if (!control) return;
        if (obrigatorio) {
            control.addValidators(Validators.required);
        } else {
            control.removeValidators(Validators.required);
        }
        control.updateValueAndValidity({ emitEvent: false });
    }

    public get icmsSnForm(): FormGroup {
        return this.form.get('icmsSn') as FormGroup;
    }

    public get isAtivoControl(): FormControl {
        return this.icmsSnForm.get('isAtivo') as FormControl;
    }

    public get csosnValue(): string | null {
        return this.icmsSnForm?.get('csosn')?.value ?? null;
    }

    /** Tipo de operação da natureza (enum inteiro), lido do form raiz. */
    public get tipoOperacaoValue(): TipoOperacaoEnum {
        return this.form?.get('tipoOperacao')?.value ?? TipoOperacaoEnum.Saida;
    }

    public get formulasParaCsosn(): FormulaCatalogoItemStruct[] {
        return this.formulasParaCodigo(this.csosnValue);
    }

    private formulasParaCodigo(codigo: string | null): FormulaCatalogoItemStruct[] {
        if (!codigo) return this.formulasIcmsSn;
        return selecionarFormulasPorCriterio(this.formulasIcmsSn, 'csosn_icms', codigo);
    }

    /** Fonte única em memória das fórmulas: o FormGroup, que o salvar() também lê.
        O @Input estadosFormulas serve só de seed inicial (o pai já popula o control). */
    private get estadosFormulasMemoria(): FormulaItemStruct[] {
        return (this.icmsSnForm.get('_estadosFormulas')?.value as FormulaItemStruct[]) ?? [];
    }

    public get personalizadasCount(): number {
        return this.estadosFormulasMemoria.filter(e => e.tipo === TipoFormulaEnum.Personalizada).length;
    }

    /** Variáveis únicas (por tag) disponíveis nas fórmulas do SN. */
    public get variaveisUnicas(): VariavelStruct[] {
        return listarVariaveisObservacao(this.formulasParaCsosn);
    }

    constructor(
        private dialog: MatDialog,
        private formulaCatalogoService: FormulaCatalogoService,
    ) {}

    private confirmarTrocaCsosn(novoCsosn: string | null): void {
        if (this.restaurandoCsosn || novoCsosn === this.csosnAnterior) return;

        const formulasAntigas = this.formulasParaCodigo(this.csosnAnterior);
        const formulasNovas = this.formulasParaCodigo(novoCsosn);
        const plano = planejarTrocaCodigoFormulas(this.estadosFormulasMemoria, formulasAntigas, formulasNovas, {
            observacao: this.icmsSnForm.get('observacao')?.value,
            observacaoFisco: this.icmsSnForm.get('observacaoFisco')?.value,
        });

        if (plano.estadosParaRestaurar.length === 0 && plano.variaveisPendentes.length === 0) {
            this.csosnAnterior = novoCsosn;
            return;
        }

        const partes = [`A troca para CSOSN ${novoCsosn ?? 'não informado'}`];
        if (plano.estadosParaRestaurar.length > 0) {
            partes.push(`restaurará ${plano.estadosParaRestaurar.length} configuraç${plano.estadosParaRestaurar.length === 1 ? 'ão' : 'ões'} de fórmulas`);
        }
        if (plano.variaveisPendentes.length > 0) {
            partes.push(`deixará pendentes as variáveis [${plano.variaveisPendentes.map(nome => `{${nome}}`).join(', ')}] nas observações`);
        }

        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirmar troca de CSOSN',
                description: `${partes.join(' e ')}. Deseja continuar?`,
                buttonConfirm: 'Continuar',
                buttonClose: 'Cancelar',
            },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result?.confirm) {
                this.restaurandoCsosn = true;
                this.icmsSnForm.get('csosn')?.setValue(this.csosnAnterior, { emitEvent: false });
                this.restaurandoCsosn = false;
                this.sincronizarObrigatoriedade();
                return;
            }

            this.icmsSnForm.get('_estadosFormulas')?.setValue(
                this.estadosFormulasMemoria.filter(estado => !plano.estadosParaRestaurar.includes(estado)));
            Object.entries(plano.observacoes).forEach(([campo, valor]) =>
                this.icmsSnForm.get(campo)?.setValue(valor));
            this.csosnAnterior = novoCsosn;
        });
    }

    public abrirEditorFormulas(): void {
        const csosn = this.csosns.find(c => c.codigo === this.csosnValue);
        const data: FormulaEditorDialogData = {
            titulo: ['Simples Nacional', this.csosnValue, csosn?.descricao].filter(Boolean).join(' - '),
            formulas: this.formulasParaCsosn.map(formula => ({
                ...formula,
                variaveis: (formula.variaveisPermitidas ?? formula.variaveis),
            })),
            estadosIniciais: this.estadosFormulasMemoria,
            estadosFormulasAgregados: coletarEstadosFormulas(this.form),
            contextoRaiz: 'natureza-operacao',
            validarPersonalizacoes: request => this.formulaCatalogoService.validarPersonalizacoes(request),
        };

        const ref = this.dialog.open(FormulaEditorDialogComponent, {
            width: '900px',
            maxWidth: '96vw',
            maxHeight: '92vh',
            data,
        });

        ref.afterClosed().subscribe((result: FormulaEditorDialogResult | null) => {
            if (result) {
                this.icmsSnForm.get('_estadosFormulas')?.setValue(result.formulas);
            }
        });
    }

    public abrirVariaveisDisponiveis(payload: VariaveisCursorPayload): void {
        const editor = payload.editor;
        const data: VariaveisDisponiveisDialogData = {
            variaveis: this.variaveisUnicas,
            targetControl: payload.control,
            cursorStart: payload.selectionStart,
            cursorEnd: payload.selectionEnd,
            onInsert: editor ? (v: VariavelStruct) => editor.inserirVariavel(v) : undefined,
        };

        this.dialog.open(VariaveisDisponiveisDialogComponent, {
            width: '820px',
            maxWidth: '96vw',
            maxHeight: '90vh',
            data,
        });
    }
}
